/**
 * StorageUsagePage — storage usage dashboard with tier breakdown.
 *
 * A16-N8. Follows settingsView overlay pattern.
 *
 * Props:
 *  - onClose: () => void
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../lib/apiClient';

const API_PREFIX = '/api/v1/aif/brain-dump';

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const idx = Math.min(Math.floor(Math.log(Math.abs(bytes)) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, idx);
  return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${units[idx]}`;
}

function formatEur(eur) {
  return `\u20AC${(eur || 0).toFixed(4)}`;
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
  } catch { return ''; }
}

const TIER_THEME = {
  hot: { label: 'Hot \u2014 aktiv', color: 'bg-[var(--aif-color-tier-hot)]', bg: 'bg-[var(--aif-color-tier-hot-light)] border-[var(--aif-color-tier-hot-border)]', text: 'text-[var(--aif-color-tier-hot)]', icon: '\uD83D\uDD25', desc: 'H\u00E4ufig genutzt, sofort verf\u00FCgbar' },
  warm: { label: 'Warm \u2014 selten', color: 'bg-[var(--aif-color-tier-warm)]', bg: 'bg-[var(--aif-color-tier-warm-light)] border-[var(--aif-color-tier-warm-border)]', text: 'text-[var(--aif-color-tier-warm)]', icon: '\u2600\uFE0F', desc: '> 30 Tage ohne Zugriff' },
  cold: { label: 'Cold \u2014 Archiv', color: 'bg-[var(--aif-color-tier-cold)]', bg: 'bg-[var(--aif-color-tier-cold-light)] border-[var(--aif-color-tier-cold-border)]', text: 'text-[var(--aif-color-tier-cold)]', icon: '\u2744\uFE0F', desc: 'Langzeit-Archiv (komprimiert)' },
};

function TierCard({ tier, bytes, files, t }) {
  const th = TIER_THEME[tier];
  return (
    <div className={`border rounded-lg p-4 ${th.bg}`} data-testid={`tier-card-${tier}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-2xl">{th.icon}</span>
        <h3 className={`font-semibold text-sm ${th.text}`}>{th.label}</h3>
      </div>
      <div className="text-2xl font-bold text-[var(--aif-text-primary)] mb-1">{formatBytes(bytes)}</div>
      <div className="text-sm text-[var(--aif-text-muted)]">
        {files} {files === 1 ? t('storage.file', 'Datei') : t('storage.files', 'Dateien')}
      </div>
      <div className="text-xs text-[var(--aif-text-muted)] mt-1">{th.desc}</div>
    </div>
  );
}

function DistributionBar({ hot, warm, cold }) {
  const total = hot + warm + cold;
  if (total === 0) return null;
  const pct = (v) => (v / total) * 100;
  return (
    <div data-testid="distribution-bar">
      <div className="flex h-8 rounded-lg overflow-hidden border border-[var(--aif-border-default)]">
        {hot > 0 && <div className="bg-[var(--aif-color-tier-hot)] flex items-center justify-center text-white text-xs" style={{ width: `${pct(hot)}%` }}>{pct(hot) > 10 ? `${pct(hot).toFixed(0)}%` : ''}</div>}
        {warm > 0 && <div className="bg-[var(--aif-color-tier-warm)] flex items-center justify-center text-white text-xs" style={{ width: `${pct(warm)}%` }}>{pct(warm) > 10 ? `${pct(warm).toFixed(0)}%` : ''}</div>}
        {cold > 0 && <div className="bg-[var(--aif-color-tier-cold)] flex items-center justify-center text-white text-xs" style={{ width: `${pct(cold)}%` }}>{pct(cold) > 10 ? `${pct(cold).toFixed(0)}%` : ''}</div>}
      </div>
      <div className="flex gap-3 mt-2 text-xs text-[var(--aif-text-muted)]">
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-[var(--aif-color-tier-hot)] rounded" /> Hot</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-[var(--aif-color-tier-warm)] rounded" /> Warm</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-[var(--aif-color-tier-cold)] rounded" /> Cold</span>
      </div>
    </div>
  );
}

export default function StorageUsagePage({ onClose }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  async function load(includeHistory = false) {
    setLoading(true);
    setError(null);
    try {
      const url = includeHistory
        ? `${API_PREFIX}/storage-usage?include_history=true&history_limit=30`
        : `${API_PREFIX}/storage-usage`;
      const res = await apiClient.get(url);
      setData(res.data ?? res);
    } catch (err) {
      setError(err.message || t('storage.loadError', 'Speichernutzung konnte nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleToggleHistory() {
    const next = !showHistory;
    setShowHistory(next);
    if (next && data && !data.history) load(true);
  }

  const current = data?.current || {};
  const totalBytes = (current.hot_bytes || 0) + (current.warm_bytes || 0) + (current.cold_bytes || 0);
  const totalFiles = (current.hot_files || 0) + (current.warm_files || 0) + (current.cold_files || 0);

  return (
    <div className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-[var(--aif-bg-page)] flex flex-col animate-slideInRight" data-testid="storage-usage-page">
      {/* Header */}
      <header className="flex items-center gap-3 px-4 py-3 border-b border-[var(--aif-border-default)] flex-shrink-0">
        <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]" data-testid="storage-back">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
        </button>
        <span className="text-sm font-semibold text-[var(--aif-text-primary)]">{t('storage.title', 'Speichernutzung')}</span>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
          {loading && !data ? (
            <div className="text-[var(--aif-text-muted)] animate-pulse py-8 text-center" data-testid="storage-loading">{t('common.loading', 'Lade...')}</div>
          ) : error && !data ? (
            <div className="bg-[var(--aif-color-danger-light)] border border-[var(--aif-color-danger-border)] text-[var(--aif-color-danger)] px-3 py-2 rounded text-sm" data-testid="storage-error">{error}</div>
          ) : (
            <>
              {/* Summary */}
              <div className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-lg p-6" data-testid="storage-summary">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs text-[var(--aif-text-muted)] uppercase tracking-wide mb-1">{t('storage.totalStorage', 'Gesamt')}</div>
                    <div className="text-2xl font-bold text-[var(--aif-text-primary)]" data-testid="total-bytes">{formatBytes(totalBytes)}</div>
                    <div className="text-sm text-[var(--aif-text-muted)]" data-testid="total-files">{totalFiles} {t('storage.files', 'Dateien')}</div>
                  </div>
                  <div>
                    <div className="text-xs text-[var(--aif-text-muted)] uppercase tracking-wide mb-1">{t('storage.monthlyCost', 'Monatl. Kosten')}</div>
                    <div className="text-2xl font-bold text-[var(--aif-text-primary)]" data-testid="monthly-cost">{formatEur(current.estimated_monthly_cost_eur)}</div>
                    <div className="text-sm text-[var(--aif-text-muted)]">{t('storage.estimated', 'gesch\u00E4tzt')}</div>
                  </div>
                  <div>
                    <div className="text-xs text-[var(--aif-text-muted)] uppercase tracking-wide mb-1">{t('storage.savings', 'Sparpotenzial')}</div>
                    <div className="text-2xl font-bold text-[var(--aif-color-success)]">{current.cold_bytes > 0 ? t('storage.active', 'Aktiv') : t('storage.setup', 'Setup')}</div>
                  </div>
                </div>
              </div>

              {totalBytes === 0 ? (
                <div className="text-center py-8 text-[var(--aif-text-muted)]" data-testid="storage-empty">{t('storage.emptyState', 'Noch keine gespeicherten Dateien.')}</div>
              ) : (
                <>
                  {/* Tier cards */}
                  <div className="grid md:grid-cols-3 gap-4">
                    <TierCard tier="hot" bytes={current.hot_bytes} files={current.hot_files} t={t} />
                    <TierCard tier="warm" bytes={current.warm_bytes} files={current.warm_files} t={t} />
                    <TierCard tier="cold" bytes={current.cold_bytes} files={current.cold_files} t={t} />
                  </div>

                  {/* Distribution bar */}
                  <div className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-lg p-4">
                    <DistributionBar hot={current.hot_bytes || 0} warm={current.warm_bytes || 0} cold={current.cold_bytes || 0} />
                  </div>

                  {/* Pricing */}
                  {data?.pricing && (
                    <div className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-lg p-4" data-testid="pricing-table">
                      <h3 className="text-xs font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide mb-2">{t('storage.pricingTitle', 'Preise pro GB/Monat')}</h3>
                      <table className="w-full text-sm">
                        <tbody>
                          <tr><td className="py-1 text-[var(--aif-text-primary)]">{TIER_THEME.hot.icon} Hot</td><td className="text-right font-mono text-[var(--aif-text-primary)]">{formatEur(data.pricing.hot_eur_per_gb_month)}</td></tr>
                          <tr><td className="py-1 text-[var(--aif-text-primary)]">{TIER_THEME.warm.icon} Warm</td><td className="text-right font-mono text-[var(--aif-text-primary)]">{formatEur(data.pricing.warm_eur_per_gb_month)}</td></tr>
                          <tr><td className="py-1 text-[var(--aif-text-primary)]">{TIER_THEME.cold.icon} Cold</td><td className="text-right font-mono text-[var(--aif-text-primary)]">{formatEur(data.pricing.cold_eur_per_gb_month)}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* History toggle */}
                  <div className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xs font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide">{t('storage.history', 'Verlauf')}</h3>
                      <button onClick={handleToggleHistory} className="text-xs text-[var(--aif-color-primary)] hover:underline" data-testid="history-toggle">
                        {showHistory ? t('common.hide', 'Ausblenden') : t('common.show', 'Anzeigen')}
                      </button>
                    </div>
                    {showHistory && data?.history && (
                      <div className="overflow-x-auto" data-testid="history-table">
                        <table className="w-full text-sm">
                          <thead className="text-xs text-[var(--aif-text-muted)] uppercase">
                            <tr><th className="text-left py-2">Datum</th><th className="text-right py-2">Gesamt</th><th className="text-right py-2">Hot</th><th className="text-right py-2">Warm</th><th className="text-right py-2">Cold</th><th className="text-right py-2">Kosten</th></tr>
                          </thead>
                          <tbody className="text-[var(--aif-text-primary)]">
                            {data.history.slice(0, 10).map((row, i) => (
                              <tr key={i} className="border-t border-[var(--aif-border-default)]">
                                <td className="py-2">{formatDate(row.taken_at)}</td>
                                <td className="text-right py-2">{formatBytes(row.total_bytes)}</td>
                                <td className="text-right py-2">{formatBytes(row.hot_bytes)}</td>
                                <td className="text-right py-2">{formatBytes(row.warm_bytes)}</td>
                                <td className="text-right py-2">{formatBytes(row.cold_bytes)}</td>
                                <td className="text-right py-2 font-mono">{formatEur(row.estimated_monthly_cost_eur)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Explainer */}
                  <div className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-lg p-4 text-sm text-[var(--aif-text-muted)]">
                    <p className="font-semibold text-[var(--aif-text-primary)] mb-2">{t('storage.howItWorks', 'Wie funktioniert das?')}</p>
                    <ul className="space-y-1 list-disc ml-5">
                      <li>{t('storage.explainer1', 'Neue Dateien landen im Hot-Tier (schneller Zugriff).')}</li>
                      <li>{t('storage.explainer2', 'Nach 30 Tagen ohne Zugriff \u2192 Warm-Tier (g\u00FCnstiger).')}</li>
                      <li>{t('storage.explainer3', 'Nach 1 Jahr \u2192 Cold-Tier (komprimiert, sehr g\u00FCnstig).')}</li>
                      <li>{t('storage.explainer4', 'Steuerrelevante Dokumente: 10 Jahre aufbewahrt (GoBD).')}</li>
                      <li>{t('storage.explainer5', 'Archivierte Dateien werden bei Zugriff automatisch wiederhergestellt.')}</li>
                    </ul>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
